import { Pencil, Slash, ThumbsDown, ThumbsUp } from "lucide-react";
import { useEffect, useState } from "react";
import type * as types from "../lib/types";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { ScrollArea } from "./ui/scroll-area";

interface MessageCandidate {
  id: string;
  content: string;
  feedback: types.Feedback;
  isEditing?: boolean;
}

interface MessageCandidatesModalProps {
  isOpen: boolean;
  onClose: () => void;
  candidates: { id: string; content: string }[];
  onFeedback: (message_id: string, feedback: types.Feedback) => Promise<void>;
  onSend: (message_id: string) => Promise<void>;
  onSendCustom: (message: string) => Promise<void>;
  onEdit: (message_id: string, content: string) => Promise<void>;
}

export function MessageCandidatesModal({
  isOpen,
  onClose,
  candidates,
  onFeedback,
  onSend,
  onSendCustom,
  onEdit,
}: MessageCandidatesModalProps) {
  const [messageCandidates, setMessageCandidates] = useState<MessageCandidate[]>([]);
  const [customMessage, setCustomMessage] = useState("");
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState<{ id: string; content: string } | undefined>(undefined);

  useEffect(() => {
    setMessageCandidates(
      candidates.map((candidate) => ({
        id: candidate.id,
        content: candidate.content,
        feedback: "neutral" as const,
        isEditing: false,
      })),
    );
    setEditingContent(undefined);
  }, [candidates]);

  const handleEdit = (id: string) => {
    setMessageCandidates((prev) =>
      prev.map((candidate) =>
        candidate.id === id ? { ...candidate, isEditing: true } : { ...candidate, isEditing: false },
      ),
    );
    setEditingContent({
      id,
      content: messageCandidates.find((c) => c.id === id)?.content || "",
    });
  };

  const handleSaveEdit = async (id: string) => {
    if (editingContent && editingContent.id === id) {
      await onEdit(id, editingContent.content);
      setMessageCandidates((prev) =>
        prev.map((candidate) =>
          candidate.id === id ? { ...candidate, content: editingContent.content, isEditing: false } : candidate,
        ),
      );
      setEditingContent(undefined);
    }
  };

  const handleCancelEdit = (id: string) => {
    setMessageCandidates((prev) =>
      prev.map((candidate) => (candidate.id === id ? { ...candidate, isEditing: false } : candidate)),
    );
    setEditingContent(undefined);
  };

  const handleFeedbackChange = async (id: string, feedback: types.Feedback) => {
    await onFeedback(id, feedback);
    setMessageCandidates((prev) =>
      prev.map((candidate) => (candidate.id === id ? { ...candidate, feedback } : candidate)),
    );
  };

  const handleSend = async () => {
    const selectedMessage = messageCandidates.find((candidate) => candidate.id === selectedMessageId);
    if (selectedMessage) {
      await onFeedback(selectedMessage.id, "chosen");
      await onSend(selectedMessage.id);
    } else if (customMessage.trim()) {
      await onSendCustom(customMessage.trim());
    }
    setCustomMessage("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>メッセージ候補</DialogTitle>
        </DialogHeader>
        <ScrollArea className="flex-grow mb-4 h-[50vh]">
          {messageCandidates.map((candidate) => (
            <div
              key={candidate.id}
              className={`p-2 rounded ${
                selectedMessageId === candidate.id ? "bg-blue-100 border border-blue-500" : "hover:bg-gray-100"
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                {candidate.isEditing ? (
                  <div className="flex-grow mr-4">
                    <Input
                      value={editingContent?.id === candidate.id ? editingContent.content : ""}
                      onChange={(e) =>
                        setEditingContent((prev) =>
                          prev?.id === candidate.id ? { ...prev, content: e.target.value } : prev,
                        )
                      }
                      className="w-full"
                    />
                  </div>
                ) : (
                  <div className="flex-grow mr-4">{candidate.content}</div>
                )}
                <div className="flex space-x-2">
                  {candidate.isEditing ? (
                    <>
                      <Button size="sm" variant="outline" onClick={() => handleSaveEdit(candidate.id)}>
                        保存
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleCancelEdit(candidate.id)}>
                        キャンセル
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button size="sm" variant="outline" onClick={() => handleEdit(candidate.id)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant={candidate.feedback === "chosen" ? "default" : "outline"}
                        onClick={() => handleFeedbackChange(candidate.id, "chosen")}
                      >
                        <ThumbsUp className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant={candidate.feedback === "rejected" ? "default" : "outline"}
                        onClick={() => handleFeedbackChange(candidate.id, "rejected")}
                      >
                        <ThumbsDown className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant={candidate.feedback === "neutral" ? "default" : "outline"}
                        onClick={() => handleFeedbackChange(candidate.id, "neutral")}
                      >
                        <Slash className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
              <Button
                variant={selectedMessageId === candidate.id ? "default" : "outline"}
                className="w-full"
                onClick={() => setSelectedMessageId(candidate.id)}
                disabled={candidate.isEditing}
              >
                {selectedMessageId === candidate.id ? "選択済み" : "選択"}
              </Button>
            </div>
          ))}
        </ScrollArea>
        <div className="flex items-center space-x-2 mb-4">
          <Input
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            placeholder="カスタムメッセージを入力"
            className="flex-grow"
          />
          <Button onClick={() => setSelectedMessageId(null)}>選択解除</Button>
        </div>
        <DialogFooter>
          <Button onClick={onClose} variant="outline">
            キャンセル
          </Button>
          <Button onClick={handleSend} disabled={!selectedMessageId && !customMessage.trim()}>
            送信
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
